import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Container, Box, Typography, Link } from '@mui/material';
import type { Components } from 'react-markdown';

const DocsPage = (): React.JSX.Element => {
  const { '*': page } = useParams<{ '*': string }>();
  const location = useLocation();
  const [content, setContent] = useState('');

  // Construct the full path for the markdown file
  useEffect(() => {
    if (!page) return;
    fetch(`/docs/${page}.md`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Markdown file not found');
        }
        return response.text();
      })
      .then((text) => setContent(text))
      .catch((error) => {
        console.error('Error loading markdown file:', error);
        setContent(
          '# Page Not Found\nThe requested documentation page does not exist.',
        );
      });
  }, [page]);

  // Handle initial hash navigation when page loads
  useEffect(() => {
    if (content && location.hash) {
      // Sanitize hash to prevent XSS - only allow alphanumeric, hyphens, and underscores
      // Extract and sanitize outside setTimeout to break taint flow
      // location.hash includes the '#' character, so we remove it
      const hash = location.hash.substring(1);
      const sanitizedHash = hash.replace(/[^a-zA-Z0-9_-]/g, '');

      if (sanitizedHash) {
        // Wait a bit for the content to render, then scroll to the hash
        const timer = setTimeout(() => {
          const element = document.getElementById(sanitizedHash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [content, location.hash]);

  // Handle internal hash links
  const handleInternalLink = (href: string): boolean => {
    if (href.startsWith('#')) {
      // Sanitize hash to prevent XSS - only allow alphanumeric, hyphens, and underscores
      const targetId = href.substring(1);
      const sanitizedTargetId = targetId.replace(/[^a-zA-Z0-9_-]/g, '');
      if (!sanitizedTargetId) {
        return true; // Invalid hash, allow default behavior
      }
      const element = document.getElementById(sanitizedTargetId);
      if (element) {
        const appBarHeight = 64; // Adjust this value to match your AppBar height (in pixels)
        const offset = appBarHeight; // Additional offset if needed
        element.scrollIntoView({ behavior: 'smooth' });
        // Apply offset to account for AppBar
        window.scrollBy({ top: -offset, behavior: 'smooth' });
        // Update URL hash without triggering scroll
        window.history.replaceState(null, '', href);
        return false; // Prevent default behavior
      }
    }
    return true; // Allow default behavior for external links
  };

  // Custom renderers for react-markdown to use MUI components
  const renderers: Components = {
    h1: ({ children, ...props }) => (
      <Typography
        variant='h4'
        gutterBottom
        id={props.id || children?.toString().toLowerCase().replace(/\s+/g, '-')}
      >
        {children}
      </Typography>
    ),
    h2: ({ children, ...props }) => (
      <Typography
        variant='h5'
        gutterBottom
        id={props.id || children?.toString().toLowerCase().replace(/\s+/g, '-')}
      >
        {children}
      </Typography>
    ),
    h3: ({ children, ...props }) => (
      <Typography
        variant='h6'
        gutterBottom
        id={props.id || children?.toString().toLowerCase().replace(/\s+/g, '-')}
      >
        {children}
      </Typography>
    ),
    h4: ({ children, ...props }) => (
      <Typography
        variant='h6'
        gutterBottom
        id={props.id || children?.toString().toLowerCase().replace(/\s+/g, '-')}
      >
        {children}
      </Typography>
    ),
    h5: ({ children, ...props }) => (
      <Typography
        variant='h6'
        gutterBottom
        id={props.id || children?.toString().toLowerCase().replace(/\s+/g, '-')}
      >
        {children}
      </Typography>
    ),
    h6: ({ children, ...props }) => (
      <Typography
        variant='h6'
        gutterBottom
        id={props.id || children?.toString().toLowerCase().replace(/\s+/g, '-')}
      >
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography variant='body1' paragraph>
        {children}
      </Typography>
    ),
    ul: ({ children }) => <ul style={{ paddingLeft: '20px' }}>{children}</ul>,
    li: ({ children }) => <li style={{ marginBottom: '8px' }}>{children}</li>,
    a: ({ href, children }) => (
      <Link
        href={href}
        onClick={(e) => {
          if (href && !handleInternalLink(href)) {
            e.preventDefault();
          }
        }}
        sx={{ textDecoration: 'none' }}
      >
        {children}
      </Link>
    ),
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ px: 2 }}>
        <ReactMarkdown components={renderers} remarkPlugins={[remarkGfm]}>
          {content}
        </ReactMarkdown>
      </Box>
    </Container>
  );
};

export default DocsPage;
